import type { AuthMethod, ScopeType, UserStatus, UserStatusReason } from '../enums';

export interface AuthUserDto {
  id: string;
  email: string;
  displayName: string | null;
  status: UserStatus;
  lastLoginAt: string | null;
  mfaEnabled: boolean;
}

export interface LoginResponseDto {
  accessToken: string;
  tokenType: 'Bearer';
  /** Access-token lifetime in seconds. */
  expiresIn: number;
  user: AuthUserDto;
}

/**
 * The password was right and the account has two-step sign-in (ADR-068): send a code with this
 * token to `POST /auth/login/mfa` within `expiresIn` seconds. No session exists yet.
 */
export interface MfaChallengeDto {
  mfaRequired: true;
  mfaToken: string;
  expiresIn: number;
}

/** What `POST /auth/login` answers. */
export type LoginResultDto = LoginResponseDto | MfaChallengeDto;

export function isMfaChallenge(result: LoginResultDto): result is MfaChallengeDto {
  return (result as MfaChallengeDto).mfaRequired === true;
}

/** The caller's two-step sign-in. */
export interface MfaStatusDto {
  /** The server can do it (`security.mfa_enabled` and an encryption key). */
  available: boolean;
  enabled: boolean;
  enabledAt: string | null;
  /** A role of the caller requires it (`security.mfa_required_roles`). */
  required: boolean;
  recoveryCodesLeft: number;
}

/** What the authenticator app needs: the QR code's URI, and the secret to type in by hand. */
export interface MfaSetupDto {
  otpauthUri: string;
  secret: string;
  issuer: string;
  account: string;
}

/** One-time recovery codes, shown once. */
export interface MfaRecoveryCodesDto {
  codes: string[];
}

export interface RoleAssignmentDto {
  id: string;
  roleId: string;
  roleCode: string;
  roleName: string;
  scopeType: ScopeType;
  collegeId: string | null;
  collegeName: string | null;
  departmentId: string | null;
  departmentName: string | null;
  assignedAt: string;
  /** MANUAL: given by an administrator; SSO: given by the provider group `ssoGroup` and kept in step with it at each single sign-on (ADR-067). */
  source: 'MANUAL' | 'SSO';
  ssoGroup: string | null;
}

/** Resolved organizational reach of a scoped permission for the current user. */
export interface EffectiveScopeDto {
  all: boolean;
  collegeIds: string[];
  departmentIds: string[];
}

export interface MeFacultyDto {
  id: string;
  employeeNumber: string;
  fullName: string;
  employmentTypeCode: string;
  attendanceModel: string;
  departmentId: string | null;
  collegeId: string | null;
}

export interface MeDto {
  user: AuthUserDto;
  roles: RoleAssignmentDto[];
  permissions: string[];
  scopes: Record<string, EffectiveScopeDto>;
  faculty: MeFacultyDto | null;
  institution: { id: string; name: string; timezone: string } | null;
  /** A role requires two-step sign-in and it is not set up: until it is, the session has no permissions (ADR-068). */
  mfaSetupRequired: boolean;
}

export interface AuthSessionDto {
  id: string;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
  revokedAt: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  deviceId: string | null;
  /** Signed in with the password or through single sign-on (Phase 3E). */
  authMethod: AuthMethod;
  current: boolean;
}

export interface UserDto extends AuthUserDto {
  createdAt: string;
  updatedAt: string;
  lockedUntil: string | null;
  failedLoginCount: number;
  roles: RoleAssignmentDto[];
  facultyProfileId: string | null;
  /** Why the system made the account inactive (EMPLOYMENT_ENDED: separated or made inactive, Phase 3D). */
  statusReason: UserStatusReason | null;
  /** Identity-provider accounts linked for single sign-on (Phase 3E). */
  identities: UserIdentityDto[];
}

/** An identity-provider account linked to a user (Phase 3E, ADR-060). */
export interface UserIdentityDto {
  id: string;
  /** The provider's issuer URL. */
  issuer: string;
  /** The person's stable identifier at the provider (`sub`). */
  subject: string;
  /** The e-mail address the provider gave when the account was linked. */
  email: string | null;
  linkedAt: string;
  lastLoginAt: string | null;
}

/** What the sign-in page needs to know about single sign-on (public). */
export interface SsoConfigDto {
  enabled: boolean;
  /** Button text: "Continue with <displayName>". */
  displayName: string | null;
  /** False when some accounts must use single sign-on (the password is refused for them). */
  passwordForEveryone: boolean;
}

export interface LogoutResponseDto {
  loggedOut: true;
  /** For single sign-on sessions: the identity provider's sign-out page, to end its session too. */
  ssoLogoutUrl: string | null;
}

export interface RoleDto {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  active: boolean;
  permissionCodes: string[];
  assignmentCount: number;
}

export interface PermissionDto {
  id: string;
  code: string;
  category: string;
  description: string;
  scoped: boolean;
}
